import type { CSSProperties } from "react";

import {
  formatAbsolute,
  formatRelative,
  formatTooltip,
} from "../../lib/datetime";

interface Props {
  /** ISO-8601 timestamp string */
  iso: string;
  /**
   * Layout. "stacked" = two lines (absolute primary, relative secondary).
   * "inline" = single line "May 21, 14:32 · 3 hours ago".
   * "compact" = single line just absolute.
   */
  variant?: "stacked" | "inline" | "compact";
  /** Optional alignment for stacked/inline */
  align?: "left" | "right";
  style?: CSSProperties;
  /** Override colors when used on dark accent surfaces */
  primaryColor?: string;
  secondaryColor?: string;
}

/**
 * Canonical timestamp display. Operators should always be able to
 * answer "when did this happen" without hovering. Two-line treatment
 * (absolute + relative) is the default; compact and inline variants
 * for tighter layouts.
 */
export function Timestamp({
  iso,
  variant = "stacked",
  align = "right",
  style,
  primaryColor = "var(--text-primary)",
  secondaryColor = "var(--text-muted)",
}: Props) {
  const absolute = formatAbsolute(iso);
  const relative = formatRelative(iso);
  const tooltip = formatTooltip(iso);

  if (variant === "compact") {
    return (
      <span
        title={tooltip}
        style={{
          fontSize: 13,
          color: primaryColor,
          textAlign: align,
          ...style,
        }}
      >
        {absolute}
      </span>
    );
  }

  if (variant === "inline") {
    return (
      <span
        title={tooltip}
        style={{
          fontSize: 13,
          color: primaryColor,
          textAlign: align,
          ...style,
        }}
      >
        {absolute}
        <span style={{ color: secondaryColor, marginLeft: 6 }}>
          · {relative}
        </span>
      </span>
    );
  }

  // stacked (default)
  return (
    <span
      title={tooltip}
      style={{
        display: "inline-flex",
        flexDirection: "column",
        alignItems: align === "right" ? "flex-end" : "flex-start",
        gap: 2,
        textAlign: align,
        ...style,
      }}
    >
      <span style={{ fontSize: 13, color: primaryColor, lineHeight: 1.25 }}>
        {absolute}
      </span>
      <span style={{ fontSize: 11.5, color: secondaryColor, lineHeight: 1.2 }}>
        {relative}
      </span>
    </span>
  );
}
